import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCESS,
  fmtDate,
  IN_FINANCE_PIPELINE,
  money,
  periodLabel,
  type ClaimStatus,
  type User,
} from '@gdec/shared';
import { api } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthContext';
import { StatusBadge } from '@/components/Badge';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { LoadingBlock } from '@/components/Spinner';
import { StatCards, type StatCard } from '@/components/StatCards';
import { useAsync } from '@/hooks/useAsync';
import type {
  ApprovalQueueRow,
  FinanceReviewQueueRow,
  MyClaimRow,
} from '@/types/claims';

/**
 * The dashboard is role-shaped: what a requester needs to see and what a
 * treasury officer needs to see have almost nothing in common. Rather than one
 * endpoint that returns a union, each variant composes the queue it already has
 * permission to read — so the dashboard can never show a number the user could
 * not have obtained by opening the corresponding page.
 */
export function DashboardPage(): ReactNode {
  const user = useCurrentUser();

  switch (user.systemAccess) {
    case ACCESS.FINANCE_REVIEW:
      return <FinanceReviewDashboard user={user} />;
    case ACCESS.MANAGER_APPROVER:
    case ACCESS.LEAD_APPROVER:
    case ACCESS.EXECUTIVE_APPROVER:
      return <ApproverDashboard user={user} />;
    case ACCESS.MAIN_ACCESS:
      return <MainAccessDashboard user={user} />;
    default:
      return <RequestorDashboard user={user} />;
  }
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

function QuickActions({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="panel">
      <h3>
        Quick Actions
        <div className="actions">{children}</div>
      </h3>
    </div>
  );
}

function NewClaimButton({ ghost = false }: { ghost?: boolean }): ReactNode {
  return (
    <Link className={ghost ? 'btn btn-ghost' : 'btn btn-primary'} to="/claim-new">
      🧾 {ghost ? 'My Own Claim' : 'New Expense Claim Request'}
    </Link>
  );
}

const myClaimColumns: ReadonlyArray<Column<MyClaimRow>> = [
  { key: 'id', header: 'Request ID', cell: (r) => <Link to={`/my-requests?claim=${r.id}`}>{r.id}</Link> },
  { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
  { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  { key: 'amount', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
  { key: 'created', header: 'Submitted', cell: (r) => fmtDate(r.createdAt) },
];

function MyRequestsPanel({ claims }: { claims: readonly MyClaimRow[] }): ReactNode {
  return (
    <div className="panel">
      <h3>
        My Own Requests
        <div className="actions">
          <Link className="btn btn-ghost btn-sm" to="/my-requests">
            View all
          </Link>
        </div>
      </h3>
      <DataTable
        rows={claims}
        columns={myClaimColumns}
        rowKey={(r) => r.id}
        empty={<EmptyState icon="🧾" title="You have not submitted a claim yet." />}
      />
    </div>
  );
}

/** Every dashboard shows the user's own claims, so every variant loads them. */
function useMyClaims() {
  return useAsync<MyClaimRow[]>(() => api.claims.mine<MyClaimRow[]>(), []);
}

function Loading(): ReactNode {
  return <LoadingBlock label="Loading your dashboard…" />;
}

function LoadError({ message }: { message: string }): ReactNode {
  return <EmptyState icon="⚠️" title="Could not load the dashboard." description={message} />;
}

/* ------------------------------------------------------------------ */
/* Requestor                                                           */
/* ------------------------------------------------------------------ */

function RequestorDashboard({ user }: { user: User }): ReactNode {
  const { data, loading, error } = useMyClaims();

  if (loading) return <Loading />;
  if (error) return <LoadError message={error} />;
  const claims = data ?? [];

  const count = (predicate: (s: ClaimStatus) => boolean) =>
    claims.filter((c) => predicate(c.status)).length;

  const cards: StatCard[] = [
    { n: claims.length, l: 'Total Claims' },
    { n: count((s) => s === 'For Checking' || s === 'For Approval'), l: 'Pending' },
    { n: count((s) => s === 'Disapproved'), l: 'Disapproved' },
    { n: count((s) => IN_FINANCE_PIPELINE.includes(s) || s === 'Released'), l: 'Approved' },
    { n: count((s) => IN_FINANCE_PIPELINE.includes(s)), l: 'In Finance Pipeline' },
    { n: count((s) => s === 'Released'), l: 'Released', accent: true },
  ];

  const disapproved = claims.filter((c) => c.status === 'Disapproved');

  return (
    <>
      <StatCards cards={cards} />

      {disapproved.length > 0 ? (
        <div className="panel">
          <h3>Needs your attention</h3>
          {disapproved.map((c) => (
            <p key={c.id} style={{ fontSize: '12.5px' }}>
              <b>{c.id}</b> was disapproved: “{c.disapprovalReason}”{' '}
              <Link className="btn btn-gold btn-sm" to={`/claim-new?resubmit=${c.id}`}>
                Edit and resubmit
              </Link>
            </p>
          ))}
        </div>
      ) : null}

      <QuickActions>
        <NewClaimButton />
      </QuickActions>

      <MyRequestsPanel claims={claims.slice(0, 6)} />
      {user.reportsTo ? null : (
        <p className="hint">
          You have no manager set in the organization chart. Claims you submit will route to an
          Executive Approver.
        </p>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Business approver                                                   */
/* ------------------------------------------------------------------ */

function ApproverDashboard({ user }: { user: User }): ReactNode {
  const mine = useMyClaims();
  const queue = useAsync<ApprovalQueueRow[]>(() => api.approvals.queue<ApprovalQueueRow[]>(), []);

  if (mine.loading || queue.loading) return <Loading />;
  const error = mine.error ?? queue.error;
  if (error) return <LoadError message={error} />;

  const pending = queue.data ?? [];
  const cards: StatCard[] = [
    { n: pending.filter((c) => c.myRole === 'Checked By').length, l: 'Pending Checking' },
    { n: pending.filter((c) => c.myRole === 'Approved By').length, l: 'Pending Approval' },
    { n: pending.length, l: 'Total Awaiting Me', accent: true },
  ];

  const columns: ReadonlyArray<Column<ApprovalQueueRow>> = [
    { key: 'id', header: 'Request ID', cell: (r) => <Link to={`/approvals?claim=${r.id}`}>{r.id}</Link> },
    { key: 'who', header: 'Requester', cell: (r) => `${r.requesterName} · ${r.requesterDepartment}` },
    { key: 'role', header: 'My Step', cell: (r) => r.myRole },
    { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
    { key: 'amount', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
    { key: 'created', header: 'Submitted', cell: (r) => fmtDate(r.createdAt) },
  ];

  return (
    <>
      <StatCards cards={cards} />
      <QuickActions>
        <Link className="btn btn-primary" to="/approvals">
          ✅ Approval Center
        </Link>
        <NewClaimButton ghost />
      </QuickActions>

      <div className="panel">
        <h3>Awaiting My Action</h3>
        <DataTable
          rows={pending}
          columns={columns}
          rowKey={(r) => r.id}
          empty={<EmptyState icon="✅" title="Nothing is waiting on you." />}
        />
      </div>

      <MyRequestsPanel claims={(mine.data ?? []).slice(0, 4)} />
      <p className="hint">Signed in as {user.name}.</p>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Finance — Review                                                    */
/* ------------------------------------------------------------------ */

function FinanceReviewDashboard({ user }: { user: User }): ReactNode {
  const mine = useMyClaims();
  const queue = useAsync<FinanceReviewQueueRow[]>(
    () => api.financeReview.queue<FinanceReviewQueueRow[]>(),
    [],
  );

  if (mine.loading || queue.loading) return <Loading />;
  const error = mine.error ?? queue.error;
  if (error) return <LoadError message={error} />;

  const rows = queue.data ?? [];
  const unowned = rows.filter((r) => r.status === 'For Finance Review');
  const mineOwned = rows.filter((r) => r.financeReviewerId === user.id);

  const cards: StatCard[] = [
    { n: unowned.length, l: 'Awaiting Review' },
    { n: mineOwned.length, l: 'My Review Queue' },
    { n: money(sumOf(rows, (r) => r.claimedAmount)), l: 'Value in Queue', accent: true },
  ];

  return (
    <>
      <StatCards cards={cards} />
      <QuickActions>
        <Link className="btn btn-primary" to="/finance-review">
          🔎 Finance — Review &amp; Approval
        </Link>
        <Link className="btn btn-ghost" to="/financial-dashboard">
          📊 Financial Dashboard
        </Link>
        <Link className="btn btn-ghost" to="/exchange-rates">
          💱 Exchange Rates
        </Link>
        <Link className="btn btn-ghost" to="/employee-finance">
          💳 Allowances &amp; SAP codes
        </Link>
        <NewClaimButton ghost />
      </QuickActions>

      <div className="panel">
        <h3>Awaiting Review &amp; Approval (unclaimed)</h3>
        <FinanceQueueTable rows={unowned} to="/finance-review" />
      </div>

      <MyRequestsPanel claims={(mine.data ?? []).slice(0, 4)} />
    </>
  );
}

/** The unclaimed queue, as much of it as fits on a dashboard. */
function FinanceQueueTable({
  rows,
  to,
}: {
  rows: readonly FinanceReviewQueueRow[];
  to: string;
}): ReactNode {
  const columns: ReadonlyArray<Column<FinanceReviewQueueRow>> = [
    { key: 'id', header: 'Request ID', cell: (r) => <Link to={`${to}?claim=${r.id}`}>{r.id}</Link> },
    { key: 'who', header: 'Requester', cell: (r) => `${r.requesterName} · ${r.requesterDepartment}` },
    { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
    { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
    {
      key: 'allowance',
      header: 'Allowance',
      align: 'right',
      cell: (r) => (r.requesterAllowance == null ? '—' : money(r.requesterAllowance)),
    },
    { key: 'created', header: 'Submitted', cell: (r) => fmtDate(r.createdAt) },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.id}
      empty={<EmptyState icon="🔎" title="The queue is empty." />}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Main Access                                                         */
/* ------------------------------------------------------------------ */

function MainAccessDashboard({ user }: { user: User }): ReactNode {
  const mine = useMyClaims();
  const overview = useAsync<import('@/types/claims').FinancialDashboardData>(
    () => api.reporting.financialDashboard<import('@/types/claims').FinancialDashboardData>(),
    [],
  );

  if (mine.loading || overview.loading) return <Loading />;
  const error = mine.error ?? overview.error;
  if (error) return <LoadError message={error} />;

  const totals = overview.data?.totals;
  const byStatus = overview.data?.byStatus ?? [];
  const countOf = (statuses: readonly ClaimStatus[]) =>
    byStatus.filter((s) => statuses.includes(s.status)).reduce((sum, s) => sum + s.count, 0);

  const cards: StatCard[] = [
    { n: totals?.claimCount ?? 0, l: 'Total Claims' },
    { n: countOf(['For Checking', 'For Approval']), l: 'In Business Approval' },
    { n: countOf(IN_FINANCE_PIPELINE), l: 'In Finance Pipeline' },
    { n: countOf(['Disapproved']), l: 'Disapproved' },
    { n: money(totals?.claimedTotal ?? 0), l: 'Total Claimed' },
    { n: money(totals?.releasedTotal ?? 0), l: 'Total Released', accent: true },
  ];

  const columns: ReadonlyArray<Column<{ status: ClaimStatus; count: number; claimedTotal: number }>> =
    [
      { key: 'status', header: 'Stage', cell: (r) => <StatusBadge status={r.status} /> },
      { key: 'count', header: 'Claims', align: 'right', cell: (r) => r.count },
      { key: 'value', header: 'Claimed Value', align: 'right', cell: (r) => money(r.claimedTotal) },
    ];

  return (
    <>
      <StatCards cards={cards} />
      <QuickActions>
        <Link className="btn btn-primary" to="/financial-dashboard">
          📊 Financial Dashboard
        </Link>
        <Link className="btn btn-ghost" to="/exchange-rates">
          💱 Exchange Rates
        </Link>
        <Link className="btn btn-ghost" to="/org-chart">
          🗂 Organization Chart
        </Link>
        <Link className="btn btn-ghost" to="/access-mgmt">
          🔑 Access Management
        </Link>
        <Link className="btn btn-ghost" to="/audit-trail">
          🕒 Audit Trail
        </Link>
      </QuickActions>

      <div className="panel">
        <h3>Where every claim is sitting</h3>
        <DataTable
          rows={byStatus}
          columns={columns}
          rowKey={(r) => r.status}
          empty={<EmptyState icon="📊" title="No claims have been submitted yet." />}
        />
      </div>

      <MyRequestsPanel claims={(mine.data ?? []).slice(0, 4)} />
      <p className="hint">Signed in as {user.name} (Main Access).</p>
    </>
  );
}

function sumOf<T>(rows: readonly T[], pick: (row: T) => number): number {
  return rows.reduce((total, row) => total + pick(row), 0);
}
